import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { keyframes } from "@mui/system";

import { describeCaller } from "./callerInfo";
import { LIVE_HISTORY_STATUSES } from "./historyFilters";
import { isActiveState, STATE_LABEL } from "./syncFormat";
import { queueLabel, taskLabel } from "./syncNames";
import { useTaskOutcome } from "./useTaskOutcome";

export const GREEN = "#86BC25";

const PILL = {
  ok: { bg: "#f0f9e6", color: "#4d7a10" },
  warn: { bg: "#fff7ed", color: "#c2500a" },
  err: { bg: "#fef2f2", color: "#b91c1c" },
  neutral: { bg: "#f1f5f9", color: "#64748b" },
  blue: { bg: "#eef6ff", color: "#0969da" },
  purple: { bg: "#f3effa", color: "#7B5EA7" },
};

const blink = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.25; transform: scale(0.8); }
`;

// A small blinking dot in the current text colour: something is still going. It stays still for people who have asked
// their system for reduced motion.
export const LiveDot = () => (
  <Box
    component="span"
    data-testid="live-dot"
    aria-hidden
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "currentColor",
      display: "inline-block",
      mr: 0.75,
      verticalAlign: "middle",
      animation: `${blink} 1.1s ease-in-out infinite`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }}
  />
);

export const Pill = ({ tone = "neutral", live = false, children, ...rest }) => (
  <Chip
    size="small"
    label={live ? <><LiveDot />{children}</> : children}
    sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, backgroundColor: PILL[tone].bg, color: PILL[tone].color }}
    {...rest}
  />
);

const STATE_TONE = { PENDING: "neutral", RECEIVED: "neutral", STARTED: "blue", RETRY: "warn", SUCCESS: "ok", FAILURE: "err", REVOKED: "neutral", ERROR: "warn" };

// The raw Celery state, with a plain-language label on hover/in text.
// `still` shows the label without the blinking dot (for legends and examples, which aren't running).
export const StatePill = ({ state, still = false }) => (
  <Pill tone={STATE_TONE[state] || "neutral"} live={!still && Boolean(state) && isActiveState(state)} data-testid="task-state" title={STATE_LABEL[state]}>
    {state || "—"}
  </Pill>
);

const HISTORY_TONE = { submitted: "neutral", started: "blue", success: "ok", failure: "err", retry: "warn", revoked: "neutral" };

// A STORED run's state. History uses lowercase values (submitted, started, success, ...), which are not the live
// status endpoint's uppercase Celery states, so they are shown as they are stored.
export const HistoryStatusPill = ({ status }) => (
  <Pill tone={HISTORY_TONE[status] || "neutral"} live={LIVE_HISTORY_STATUSES.includes(status)} data-testid="history-status">
    {status ? status.charAt(0).toUpperCase() + status.slice(1) : "—"}
  </Pill>
);

const OUTCOME_TONE = { completed: "ok", skipped: "warn", problem: "err", dispatched: "purple", failed: "err", revoked: "neutral", unknown: "warn" };

// What the finished task actually did (a SUCCESS can still be a skip or a reported problem).
export const OutcomePill = ({ task }) => {
  const outcome = useTaskOutcome(task);
  if (outcome.kind === "running") return <Typography component="span" sx={{ color: "#94a3b8", fontSize: 12 }}>—</Typography>;
  return <Pill tone={OUTCOME_TONE[outcome.kind] || "neutral"} data-testid="task-outcome">{outcome.label}</Pill>;
};

export const ScopeChip = ({ scope }) => <Pill tone={scope.startsWith("Global") ? "warn" : scope === "User" ? "purple" : "blue"}>{scope}</Pill>;

export const Panel = ({ title, subtitle, actions, children, sx }) => (
  <Box sx={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px", ...sx }}>
    {(title || actions) && (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", p: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
          {title}
          {subtitle && <Typography component="span" sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 400, ml: 1 }}>· {subtitle}</Typography>}
        </Typography>
        {actions}
      </Box>
    )}
    {children}
  </Box>
);

export const JsonBlock = ({ value }) => (
  <Box component="pre" data-testid="json-block" sx={{ m: 0, background: "#0f172a", color: "#d6e2f0", borderRadius: "10px", p: "12px 14px", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12, overflow: "auto", maxHeight: 260 }}>
    {JSON.stringify(value, null, 2)}
  </Box>
);

// A task as people read it: the plain name, with the technical name smaller underneath.
export const TaskName = ({ name }) => (
  <Box>
    <Typography sx={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.3 }} data-testid="task-label">{taskLabel(name)}</Typography>
    <Typography sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11.5, color: "#94a3b8" }} data-testid="task-technical">{name}</Typography>
  </Box>
);

// Who triggered a run. `source` is a stored record (owner + payload.triggered_by) or an accepted response / tracked task
// (owner + triggered_by). Values are plain text. The raw owner id is on hover.
export const CallerCell = ({ source }) => {
  const caller = describeCaller(source);
  return (
    <Box title={caller.ownerId || undefined} data-testid="caller" data-kind={caller.kind}>
      <Typography sx={{ fontSize: 13, fontWeight: caller.kind === "person" ? 600 : 400, wordBreak: "break-word", color: caller.kind === "unknown" ? "#94a3b8" : "inherit", lineHeight: 1.3 }} data-testid="caller-primary">
        {caller.primary}
      </Typography>
      {caller.secondary && (
        <Typography sx={{ fontSize: 11.5, color: "#64748b", wordBreak: "break-word" }} data-testid="caller-secondary">{caller.secondary}</Typography>
      )}
    </Box>
  );
};

// A queue by its plain name; the technical name is on hover.
export const QueueName = ({ queue }) => (
  <span title={queue || undefined} data-testid="queue-label">{queueLabel(queue)}</span>
);

export const headCellSx = { background: "#f8fafc", color: "#64748b", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", py: 1.2 };
export const mono = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12, color: "#334155" };
